import { useState, useEffect, useCallback } from 'react'
import {
  Box, Typography, Button, Alert, Stack, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { formatDate } from '@shared/utils/datetime'
import { attachmentService } from '../../../../shared/services/AttachmentService'
import { AttachmentSummary } from '../../../../shared/types/entities'

const downloadUrl = (attachmentId: string) => attachmentService.getDownloadUrl(attachmentId)

/**
 * Ticket-wide attachments (portal uploads and email attachments), with inline image previews.
 * Shared by the agent and customer ticket pages.
 */
export function TicketAttachments({ ticketId }: { ticketId: string }) {
  const [attachments, setAttachments] = useState<AttachmentSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<AttachmentSummary | null>(null)

  const loadAttachments = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setAttachments(await attachmentService.getTicketAttachments(ticketId))
    } catch (err: any) {
      setError(err.message || 'Failed to load attachments')
    } finally {
      setLoading(false)
    }
  }, [ticketId])

  useEffect(() => {
    loadAttachments()
  }, [loadAttachments])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      setUploading(true)
      setError(null)
      await attachmentService.uploadAttachment(file, ticketId)
      await loadAttachments()
      e.target.value = ''
    } catch (err: any) {
      setError(err.message || 'Failed to upload attachment')
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  const inputId = `file-upload-${ticketId}`

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {attachments.length === 0 ? (
        <Typography color="textSecondary" sx={{ py: 2 }}>
          No attachments yet.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {attachments.map((att) => (
            <Box
              key={att.attachmentId}
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 2,
                alignItems: 'center'
              }}
            >
              {att.contentType?.startsWith('image/') && (
                <Box
                  component="img"
                  src={downloadUrl(att.attachmentId)}
                  alt={att.originalFileName}
                  loading="lazy"
                  onClick={() => setPreview(att)}
                  sx={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', flexShrink: 0 }}
                />
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {att.originalFileName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {att.fileSizeFormatted} • Uploaded by {att.uploadedByName} on {formatDate(att.uploadedAt)}
                </Typography>
              </Box>
              <Button size="small" onClick={() => window.open(downloadUrl(att.attachmentId), '_blank')}>
                Download
              </Button>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        {preview && (
          <>
            <DialogTitle sx={{ pr: 6, overflowWrap: 'anywhere' }}>
              {preview.originalFileName}
              <IconButton
                aria-label="close"
                onClick={() => setPreview(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'grey.900' }}>
              <Box
                component="img"
                src={downloadUrl(preview.attachmentId)}
                alt={preview.originalFileName}
                sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => window.open(downloadUrl(preview.attachmentId), '_blank')}>Download</Button>
              <Button onClick={() => setPreview(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <input type="file" onChange={handleFileSelect} style={{ display: 'none' }} id={inputId} />
      <label htmlFor={inputId}>
        <Button variant="outlined" component="span" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Attachment'}
        </Button>
      </label>
    </Box>
  )
}
